"use client";

import Link from "next/link";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";
import { IconTool, IconScale, IconClock, IconLogout } from "@tabler/icons-react";

interface MaintenanceClientProps {
  formattedEndDate: string | null;
  isAuthenticated: boolean;
  redirectTo?: string;
}

export function MaintenanceClient({ formattedEndDate, isAuthenticated, redirectTo = "/" }: MaintenanceClientProps) {
  const t = useTranslations("Landing.maintenance");
  const router = useRouter();

  useEffect(() => {
    const check = async () => {
      try {
        const res = await fetch("/api/health");
        if (res.ok) {
          router.push(redirectTo);
        }
      } catch {
        // still down
      }
    };

    const interval = setInterval(check, 10_000);
    return () => clearInterval(interval);
  }, [redirectTo, router]);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center px-6"
      style={{ background: "var(--landing-bg)" }}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg text-center"
      >
        {/* Logo */}
        <div className="mb-8 flex items-center justify-center gap-2">
          <IconScale size={28} style={{ color: "var(--landing-accent)" }} />
          <span
            className="text-xl font-semibold"
            style={{
              fontFamily: "var(--font-display), Georgia, serif",
              color: "var(--landing-text)",
            }}
          >
            LawChat
          </span>
        </div>

        {/* Icon */}
        <div
          className="mx-auto mb-8 flex size-20 items-center justify-center rounded-2xl"
          style={{
            background: "var(--landing-accent-soft)",
            border: "1px solid rgba(193,95,60,0.15)",
          }}
        >
          <IconTool size={36} style={{ color: "var(--landing-accent)" }} />
        </div>

        {/* Title */}
        <h1
          style={{
            fontFamily: "var(--font-display), Georgia, serif",
            fontSize: "clamp(1.8rem, 4vw, 2.4rem)",
            fontWeight: 500,
            color: "var(--landing-text)",
            lineHeight: 1.2,
          }}
        >
          {t("title")}
        </h1>

        {/* Subtitle */}
        <p
          className="mx-auto mt-4 max-w-md"
          style={{
            fontFamily: "var(--font-body), system-ui, sans-serif",
            fontSize: "1rem",
            lineHeight: 1.75,
            color: "var(--landing-text-secondary)",
          }}
        >
          {t("subtitle")}
        </p>

        {/* Expected end time */}
        {formattedEndDate && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mx-auto mt-8 inline-flex items-center gap-3 rounded-xl px-6 py-4"
            style={{
              background: "var(--landing-bg-secondary)",
              border: "1px solid var(--landing-border)",
            }}
          >
            <IconClock
              size={20}
              className="shrink-0"
              style={{ color: "var(--landing-accent)" }}
            />
            <div className="text-left">
              <p
                className="text-xs uppercase tracking-wider"
                style={{
                  fontFamily: "var(--font-mono-landing), monospace",
                  color: "var(--landing-text-muted)",
                  letterSpacing: "0.08em",
                  fontSize: "0.7rem",
                }}
              >
                {t("expectedEnd")}
              </p>
              <p
                className="mt-0.5 font-medium"
                style={{
                  fontFamily: "var(--font-display), Georgia, serif",
                  color: "var(--landing-text)",
                  fontSize: "0.95rem",
                }}
              >
                {formattedEndDate}
              </p>
            </div>
          </motion.div>
        )}

        {/* Bottom message */}
        <p
          className="mt-10"
          style={{
            fontFamily: "var(--font-body), system-ui, sans-serif",
            fontSize: "0.85rem",
            color: "var(--landing-text-muted)",
          }}
        >
          {t("patience")}
        </p>

        {/* Logout link for authenticated users */}
        {isAuthenticated && (
          <Link
            href="/sign-out"
            className="mt-6 inline-flex items-center gap-1.5 text-sm transition-colors"
            style={{ color: "var(--landing-text-muted)" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "var(--landing-accent)")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "var(--landing-text-muted)")}
          >
            <IconLogout size={15} />
            {t("logout")}
          </Link>
        )}
      </motion.div>
    </div>
  );
}
